import { ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { isPast, isToday } from 'date-fns';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommitmentService } from 'src/app/root/commitment.service';
import { DayCommitment, RegionCommitments, WeekCommitment } from 'src/app/types';
import { monthName } from '../month-name.pipe';

@Component({
  selector: 'lohi-commitment-calendar',
  templateUrl: './commitment-calendar.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommitmentCalendarComponent implements OnInit {
  @ViewChild('updateResponseModal') private updateResponseModal: TemplateRef<any>;
  @ViewChild('areYouSureModal') private areYouSureModal: TemplateRef<any>;
  @Input() public region: number;
  public selectedWeek: WeekCommitment;
  public selectedRegion: RegionCommitments;
  public weekForm: FormArray;
  public weeklyCommitmentsRegions$: Observable<RegionCommitments[]>;
  private updateDialogRef: MatDialogRef<any>;
  private areYouSureDialogRef: MatDialogRef<any>;
  public holidayWarning$$ = new BehaviorSubject<{
    dayList: { date: string; holidayName: string }[];
  }>(null);

  constructor(private commitmentsService: CommitmentService, private matDialog: MatDialog, private fb: FormBuilder) {}

  public ngOnInit(): void {
    this.weeklyCommitmentsRegions$ = this.commitmentsService.weeklyCommitments$.pipe(
      map(
        (response) => response?.regions.filter((value) => this.region === undefined || value.id === this.region) || [],
      ),
      map(updateSimpleDates),
    );
  }

  public validateEntries() {
    if (this.weekForm.invalid) {
      this.weekForm.markAllAsTouched();
      return;
    }
    this.checkAboveAverageOnHolidays();
    if (this.holidayWarning$$.value) {
      this.areYouSureDialogRef = this.matDialog.open(this.areYouSureModal, {
        panelClass: ['w-full', 'lg:w-1/3'],
      });
      return;
    }

    this.updateCommittedShifts();
  }

  public async updateCommittedShifts() {
    const values = (this.weekForm.value as number[]) || [];
    const responses: { requestId: number; concurrentDriverCount: number }[] = this.selectedWeek.days.map((day, i) => {
      return {
        requestId: day.requestId,
        concurrentDriverCount: values[i],
      };
    });
    const success = await this.commitmentsService.updateWeeklyCommitment(responses);
    if (success) {
      if (this.areYouSureDialogRef) {
        this.areYouSureDialogRef.close();
        this.areYouSureDialogRef = null;
      }
      if (this.updateDialogRef) {
        this.updateDialogRef.close();
        this.updateDialogRef = null;
      }
    }
  }

  public updateCommitment(region: RegionCommitments, commitment: WeekCommitment) {
    if (this.updateDialogRef) {
      this.updateDialogRef.close();
      this.updateDialogRef = null;
    }
    this.selectedWeek = commitment;
    this.selectedRegion = region;
    this.weekForm = this.fb.array(
      this.selectedWeek.days.map((day) => [day.latestResponse, [Validators.required, Validators.min(0)]]),
    );
    this.holidayWarning$$.next(null);
    this.updateDialogRef = this.matDialog.open(this.updateResponseModal, {
      panelClass: ['w-full', 'lg:w-1/2'],
    });
  }

  public checkAboveAverageOnHolidays() {
    const values = this.weekForm.value as number[];
    if (!values) {
      return null;
    }
    const average = values.reduce((sum, v) => sum + v) / values.length;
    const aboveAverageHolidays: { date: string; holidayName: string }[] = [];
    this.selectedWeek.days.forEach((day, index) => {
      if (day.holidayName && average < values[index]) {
        aboveAverageHolidays.push({
          date: `${day.dayOfWeek}, ${monthName(day.month)} ${day.day}`,
          holidayName: day.holidayName,
        });
      }
    });
    if (aboveAverageHolidays.length) {
      this.holidayWarning$$.next({
        dayList: aboveAverageHolidays,
      });
    } else {
      this.holidayWarning$$.next(null);
    }
  }
}

function updateSimpleDates(regions: RegionCommitments[]): RegionCommitments[] {
  return regions.map((region) => {
    return {
      ...region,
      weeks: region.weeks.map((week) => {
        return {
          ...week,
          days: week.days.map(updateAddTodayInfoToSimpleDate),
        };
      }),
    };
  });
}

function updateAddTodayInfoToSimpleDate(date: DayCommitment): DayCommitment {
  const jsDate = new Date(date.year, date.month - 1, date.day);
  if (isToday(jsDate)) {
    return {
      ...date,
      isToday: true,
    };
  }

  if (isPast(jsDate)) {
    return {
      ...date,
      isInThePast: true,
    };
  }
  return date;
}
